import { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Typography,
  Paper,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Common HTTP header presets
 */
const HEADER_PRESETS = [
  { key: 'User-Agent', value: 'Mozilla/5.0 (compatible; SiteAwake/1.0)' },
  { key: 'Accept', value: 'application/json' },
  { key: 'Accept', value: 'text/html' },
  { key: 'Accept', value: '*/*' },
  { key: 'Accept-Language', value: 'en-US,en;q=0.9' },
  { key: 'Accept-Encoding', value: 'gzip, deflate, br' },
  { key: 'Content-Type', value: 'application/json' },
  { key: 'Content-Type', value: 'application/x-www-form-urlencoded' },
  { key: 'Authorization', value: 'Bearer <token>' },
  { key: 'Authorization', value: 'Basic <credentials>' },
  { key: 'Cache-Control', value: 'no-cache' },
  { key: 'X-Requested-With', value: 'XMLHttpRequest' },
];

/**
 * Header row interface
 */
export interface HeaderRow {
  id: string;
  key: string;
  value: string;
}

/**
 * HeadersEditor component props
 */
interface HeadersEditorProps {
  headers: HeaderRow[];
  onChange: (headers: HeaderRow[]) => void;
  disabled?: boolean;
}

/**
 * Generate a unique ID for header rows
 */
function generateId(): string {
  return `header-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * HeadersEditor component
 * Key-value editor for HTTP headers with presets
 */
export function HeadersEditor({ headers, onChange, disabled = false }: HeadersEditorProps) {
  const [presetAnchorEl, setPresetAnchorEl] = useState<null | HTMLElement>(null);
  const presetMenuOpen = Boolean(presetAnchorEl);

  const handleAddHeader = () => {
    onChange([...headers, { id: generateId(), key: '', value: '' }]);
  };

  const handleRemoveHeader = (id: string) => {
    onChange(headers.filter((h) => h.id !== id));
  };

  const handleHeaderChange = (id: string, field: 'key' | 'value', newValue: string) => {
    onChange(headers.map((h) => (h.id === id ? { ...h, [field]: newValue } : h)));
  };

  const handlePresetClick = (event: React.MouseEvent<HTMLElement>) => {
    setPresetAnchorEl(event.currentTarget);
  };

  const handlePresetClose = () => {
    setPresetAnchorEl(null);
  };

  const handlePresetSelect = (preset: { key: string; value: string }) => {
    // Check if header with same key already exists
    const existingIndex = headers.findIndex((h) => h.key === preset.key);
    if (existingIndex >= 0) {
      // Update existing header
      onChange(headers.map((h, i) => (i === existingIndex ? { ...h, value: preset.value } : h)));
    } else {
      // Add new header
      onChange([...headers, { id: generateId(), key: preset.key, value: preset.value }]);
    }
    handlePresetClose();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Custom Headers
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={handlePresetClick}
            endIcon={<ExpandMoreIcon />}
            disabled={disabled}
          >
            Presets
          </Button>
          <Menu
            anchorEl={presetAnchorEl}
            open={presetMenuOpen}
            onClose={handlePresetClose}
            PaperProps={{
              sx: { maxHeight: 300 },
            }}
          >
            {HEADER_PRESETS.map((preset, index) => (
              <MenuItem
                key={`${preset.key}-${preset.value}-${index}`}
                onClick={() => handlePresetSelect(preset)}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {preset.key}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {preset.value.length > 40
                      ? `${preset.value.substring(0, 40)}...`
                      : preset.value}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddHeader}
            disabled={disabled}
          >
            Add
          </Button>
        </Box>
      </Box>

      {headers.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            textAlign: 'center',
            bgcolor: 'action.hover',
            borderStyle: 'dashed',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No custom headers configured
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddHeader}
            sx={{ mt: 1 }}
            disabled={disabled}
          >
            Add Header
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {headers.map((header) => (
            <Box
              key={header.id}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                placeholder="Header name"
                value={header.key}
                onChange={(e) => handleHeaderChange(header.id, 'key', e.target.value)}
                disabled={disabled}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                placeholder="Header value"
                value={header.value}
                onChange={(e) => handleHeaderChange(header.id, 'value', e.target.value)}
                disabled={disabled}
                sx={{ flex: 2 }}
              />
              <Tooltip title="Remove header">
                <IconButton
                  size="small"
                  onClick={() => handleRemoveHeader(header.id)}
                  disabled={disabled}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Convert headers array to Record format for API
 */
export function headersToRecord(headers: HeaderRow[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const header of headers) {
    if (header.key.trim()) {
      result[header.key.trim()] = header.value;
    }
  }
  return result;
}

/**
 * Convert Record format to headers array
 */
export function recordToHeaders(record: Record<string, string> | null | undefined): HeaderRow[] {
  if (!record) return [];
  return Object.entries(record).map(([key, value]) => ({
    id: generateId(),
    key,
    value,
  }));
}

export default HeadersEditor;
